import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

import { RootState, AppDispatch } from '../../store'

import { formatUser } from './helpers'
import {
  createUserChannel,
  deleteUserChannel,
  updateUserChannel
} from '../../lib/api'
import { AddErrorAlert } from '../alert'

export { formatUser } from './helpers'

type CachedPagination = IndexPagination & {
  loaded: number[]
}

type UserState = {
  loading: string[]
  users: { [id: string]: LocalUser }
  loaded: boolean
  // Keys denote external or not
  pagination: {
    true: null | CachedPagination
    false: null | CachedPagination
  }
}

const initialState: UserState = {
  loading: [],
  users: {},
  loaded: false,
  pagination: {
    true: null,
    false: null
  }
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateLoaded: (state: UserState) => {
      state.users = {}
      state.loaded = true
    },

    updateUser: (state: UserState, action: PayloadAction<LocalUser>) => {
      const user = action.payload
      const id = user.id
      state.users[id] = { ...(state.users[id] ?? {}), ...user }
    },

    updateUsers: (state: UserState, action: PayloadAction<APIUser[]>) => {
      const users = action.payload
      users.forEach(u => {
        const user = formatUser(u)
        const id = user.id
        state.users[id] = { ...(state.users[id] ?? {}), ...user }
      })
    },

    deleteUsers: (state: UserState, action: PayloadAction<string[]>) => {
      action.payload.forEach(id => {
        delete state.users[id]
      })
    },

    updateLoading: (state: UserState, action: PayloadAction<string[]>) => {
      action.payload.forEach(id => {
        if (!state.loading.includes(id)) {
          state.loading.push(id)
        }
      })
    },

    clearLoading: (state: UserState) => {
      state.loading = []
    },

    usersLoaded: (state: UserState, action: PayloadAction<LocalUser[]>) => {
      const users = action.payload
      const filtered = users.filter(user => !(user.id in state.users))
      filtered.forEach(user => {
        state.users[user.id] = user
      })
    },

    pageLoad: (
      state: UserState,
      action: PayloadAction<{
        res: WithIndexPagination<APIUser[]>
        external: boolean
        cb: Function
        page: number
      }>
    ) => {
      const { res, external, cb, page } = action.payload
      const { data, pagination } = res
      data.forEach(u => {
        state.users[u.id] = formatUser(u)
      })

      cb({ ...state.users })

      const externalString = external.toString()
      state.pagination[externalString] = {
        ...state.pagination[externalString],
        ...pagination,
        loaded: [
          ...new Set([
            ...(state.pagination[externalString]?.loaded ?? []),
            page
          ])
        ].sort((a, b) => a - b)
      }
    },

    resetPagination: (state: UserState, action: PayloadAction<boolean>) => {
      const external = action.payload
      const externalString = external.toString()
      const defaultPagination: CachedPagination = {
        page: 0,
        per_page: 25,
        total: state.pagination[externalString]?.total ?? 0,
        loaded: []
      }
      state.pagination[externalString] = defaultPagination
    },

    gotoPage: (
      state: UserState,
      action: PayloadAction<{ external: boolean; page: number }>
    ) => {
      const { external, page } = action.payload
      const externalString = external.toString()
      state.pagination[externalString] = {
        ...state.pagination[externalString],
        page
      }
    },

    merge: (
      state: UserState,
      action: PayloadAction<{ from: string; toUser: LocalUser }>
    ) => {
      const { from, toUser } = action.payload
      delete state.users[from]
      state.users[toUser.id] = toUser
    },

    setChannels: (
      state: UserState,
      action: PayloadAction<{ user: string; channels: APIChannel[] }>
    ) => {
      const { user, channels } = action.payload
      if (user in state.users) {
        state.users[user].channels = channels
      }
    },

    mockUpdateChannel: (
      state: UserState,
      action: PayloadAction<{
        user: string
        channelID?: string
        channel: ChannelUpdate
      }>
    ) => {
      const { user, channelID, channel } = action.payload
      if (!(user in state.users)) {
        return
      }

      const foundUser = state.users[user]
      // Mock channel with new data
      const newChannel: APIChannel = {
        ...channel,
        // These values will get replaced by API response
        id: uuidv4(),
        compliance: false,
        validated: '',
        deleted_at: '',
        created_at: ''
      }

      // Add new channel
      if (!channelID) {
        foundUser.channels.push(newChannel)
        return
      }

      // Find and update existing
      const idx = foundUser.channels.findIndex(ch => ch.id === channelID)
      if (idx !== -1) {
        foundUser.channels[idx] = newChannel
      }
    },

    deleteChannel: (
      state: UserState,
      action: PayloadAction<{ user: string; channel: string }>
    ) => {
      const { user, channel } = action.payload
      if (user in state.users) {
        state.users[user].channels = state.users[user].channels.filter(
          ch => ch.id !== channel
        )
      }
    }
  }
})

export const {
  updateLoaded,
  updateUser,
  updateUsers,
  deleteUsers,
  updateLoading,
  clearLoading,
  usersLoaded,
  pageLoad,
  resetPagination,
  gotoPage,
  merge
} = userSlice.actions
const {
  setChannels,
  /// mockUpdateChannel,
  deleteChannel
} = userSlice.actions

export const updateChannel = (
  userID: string,
  channel: ChannelUpdate,
  channelID: null | string,
  onSuccess?: Function,
  onFail?: Function
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const data = await (channelID
        ? updateUserChannel(userID, channelID, channel)
        : createUserChannel(userID, channel))
      dispatch(setChannels({ user: userID, channels: data }))
      onSuccess && onSuccess(data)
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
      onFail && onFail()
    }
  }
}

export const removeChannel = (userID: string, channelID: string) => {
  return async (dispatch: AppDispatch) => {
    await deleteUserChannel(userID, channelID)
    dispatch(deleteChannel({ user: userID, channel: channelID }))
  }
}

export const selectUser = (state: RootState) => state.user

export default userSlice.reducer
